import { Input, Text, Box, Flex, Avatar, Button } from "@chakra-ui/react";
import { UserAvatar } from "components/Layout/Header/UserAvatar";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import BoxShadowContainer from "components/UIComponents/BoxShadowContainer";
import "./ProfileView.css";
import { FormattedMessage } from "react-intl";
import { useMobileContext } from "context/MobileContext";
import { useCallback, useEffect, useRef, useState } from "react";
import { getAccountDetails } from "api/GoogleAuth";
import { METHOD_KEYS, SignInMethodsData } from "api/SignInMethods";
import SignInMethods, { SignInMethodsFrame } from "components/Profile/SignInMethods";
import { FOCUS_RING, TOUCH_TARGET } from "utils/chackra/controlStyles";
import { profileDisplayName } from "./profileName";

/** A status the server did not report is unknown, and unknown must never look
 *  like "free to link": an old API or a partial answer is treated as a failure. */
const readMethods = (value: unknown): SignInMethodsData | null => {
  const methods = value as Partial<SignInMethodsData> | null | undefined;
  return methods && METHOD_KEYS.every((key) => methods[key]) ? (methods as SignInMethodsData) : null;
};

const ProfileInfo = () => {
  const userState = useSelector((state: RootState) => state.userData);
  const [accountData, setAccountData] = useState<any>(null);
  // True from the first render: before the first answer the card is loading,
  // not failed, or the error line would flash on every open.
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const latestRequest = useRef(0);
  const cardHeadingRef = useRef<HTMLHeadingElement>(null);
  // Set when this fetch makes the control holding focus disappear (a linked
  // tile, a Retry button). Honoured once the answer has rendered, since the
  // answer can swap the whole card and its heading with it.
  const focusCardWhenDone = useRef(false);
  const [settledFetches, setSettledFetches] = useState(0);
  const [dialogActive, setDialogActive] = useState(false);

  const { isMobile } = useMobileContext();

  const fetchAccountData = useCallback(
    async ({ silent, focusCard = false }: { silent: boolean; focusCard?: boolean }) => {
      if (!userState.isUserLoggedIn) return;
      if (focusCard) focusCardWhenDone.current = true;
      const request = ++latestRequest.current;

      if (!userState.apiKey) {
        setLoading(false);
        setLoadFailed(true);
        setSettledFetches((count) => count + 1);
        return;
      }

      // Keeps the current card on screen while it refreshes after a link.
      // A failure stays up until an answer replaces it: accountData may be from
      // before a link, and would show that method as free to link again.
      if (!silent) setLoading(true);
      try {
        const response = await getAccountDetails(userState.apiKey);
        if (request !== latestRequest.current) return;
        if (response.status === "success") {
          setAccountData(response.data);
          setLoadFailed(false);
        } else {
          setLoadFailed(true);
        }
      } catch (error) {
        if (request === latestRequest.current) setLoadFailed(true);
      } finally {
        if (request === latestRequest.current) {
          setLoading(false);
          setSettledFetches((count) => count + 1);
        }
      }
    },
    [userState.apiKey, userState.isUserLoggedIn]
  );

  useEffect(() => {
    fetchAccountData({ silent: false });
  }, [fetchAccountData]);

  useEffect(() => {
    if (!focusCardWhenDone.current) return;
    focusCardWhenDone.current = false;
    // Only when focus has nowhere to be: removing a focused control leaves it on
    // body. Anything else means the user moved on, or a dialog holds its lock.
    const active = document.activeElement;
    if (!active || active === document.body) cardHeadingRef.current?.focus();
  }, [settledFetches]);

  // A failure swaps the card for the error line, but not under an open dialog,
  // which would vanish with what the user typed. It shows once the dialog closes.
  useEffect(() => {
    const active = document.activeElement;
    if (!dialogActive && loadFailed && (!active || active === document.body)) {
      cardHeadingRef.current?.focus();
    }
    // Only when a dialog has just gone; the fetch effect above covers the rest.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dialogActive]);

  const knownMethods = readMethods(accountData?.sign_in_methods);
  const methods = loadFailed ? null : knownMethods;
  const cardMethods = dialogActive ? knownMethods : methods;
  const userId = Number(accountData?.user_data?.user_id ?? userState.userId);
  const cardReady = cardMethods !== null && userId > 0;
  const displayName = profileDisplayName({
    google: accountData?.google_data,
    telegram: accountData?.telegram_data,
    stored: userState.userNickname,
  });

  return (
    <div className="user-info-section">
      <div className="user-info-section-avatar"></div>
      <div className="user-main-info">
        <BoxShadowContainer>
          {loading ? (
            <Text>
              <FormattedMessage id="LoadingAccountData" defaultMessage="Loading account data..." />
            </Text>
          ) : (
            <>
              <Flex mb={10} align="center" className="user-info-top">
                <Box className="user-info-section-avatar">
                  {accountData?.google_data?.picture ? (
                    <Avatar 
                      size="2xl" 
                      src={accountData.google_data.picture}
                      name={displayName}
                    />
                  ) : (
                    <UserAvatar size="2xl" />
                  )}
                </Box>
                <Box ml={!isMobile && 7}>
                  <Text fontSize="sm" textAlign="center">
                    <FormattedMessage id="ProfileSystemIdText" />
                    <br /> {accountData?.user_data?.user_id || userState.userId}
                  </Text>
                </Box>
                <Box ml={!isMobile && 7}>
                  <Text fontSize="sm" textAlign="center">
                    <FormattedMessage id="ProfileRegDateText" /> <br />{" "}
                    {accountData?.user_data?.reg_date?.split(" ")[0] || userState.regDate?.split(" ")[0]}
                  </Text>
                </Box>
              </Flex>
              
              {/* Username */}
              {displayName && (
                <Box>
                  <Text as="label" htmlFor="profile-name" display="block" mb={3} fontSize="sm">
                    <FormattedMessage id="ProfileNicknameText" />
                  </Text>
                  <Input id="profile-name" value={displayName} isReadOnly={true} />
                </Box>
              )}

              {/* Email: the server's status, so a Telegram placeholder never
                  shows. Unknown status claims nothing, not even "not set". */}
              {methods && (
                <Box mt={displayName ? 5 : 0}>
                  <Text as="label" htmlFor="profile-email" display="block" mb={3} fontSize="sm">
                    <FormattedMessage id="GoogleEmail" defaultMessage="Email" />
                  </Text>
                  <Input
                    id="profile-email"
                    value={methods.email.display ?? ""}
                    isReadOnly={true}
                    aria-describedby={methods.email.display ? undefined : "profile-email-hint"}
                  />
                  {!methods.email.display && (
                    <Text id="profile-email-hint" mt={1} fontSize="sm" color="text.secondary">
                      <FormattedMessage id="profile.emailNotSet" />
                    </Text>
                  )}
                </Box>
              )}
            </>
          )}
        </BoxShadowContainer>

        <Box mt={5}>
          {cardReady ? (
            <SignInMethods
              methods={cardMethods}
              userId={userId}
              onLinked={() => fetchAccountData({ silent: true, focusCard: true })}
              headingRef={cardHeadingRef}
              onDialogActive={setDialogActive}
            />
          ) : (
            <SignInMethodsFrame headingRef={cardHeadingRef}>
              {loading ? (
                <Text color="text.secondary">
                  <FormattedMessage id="LoadingAccountData" defaultMessage="Loading account data..." />
                </Text>
              ) : (
                <Flex role="alert" align="center" gap={3} wrap="wrap">
                  <Text color="text.primary">
                    <FormattedMessage id="signInMethods.loadFailed" />
                  </Text>
                  <Button
                    variant="outline"
                    borderColor="border.strong"
                    size="sm"
                    minH={TOUCH_TARGET}
                    _focusVisible={FOCUS_RING}
                    onClick={() => {
                      // Now, because this button leaves while the fetch runs,
                      // and again when it answers (see focusCardWhenDone).
                      cardHeadingRef.current?.focus();
                      fetchAccountData({ silent: false, focusCard: true });
                    }}
                  >
                    <FormattedMessage id="signInMethods.retry" />
                  </Button>
                </Flex>
              )}
            </SignInMethodsFrame>
          )}
        </Box>
      </div>
    </div>
  );
};

export default ProfileInfo;
